import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { useState, useEffect } from "react";
import { logAction } from "./ALQOSLogger";
import { subirDatos, eliminarDato } from "./syncSupabase";

const STORAGE_PC = 'alqos_plancontrol';
const STORAGE_FMEA = 'alqos_fmea';
const STORAGE_NC = 'alqos_ncs';
const STORAGE_MAQUINAS = 'alqos_maquinas';

const LOG = {
  info: (msg, data) => console.log(`[PLAN-CONTROL][INFO] ${msg}`, data || ''),
  error: (msg, err) => console.error(`[PLAN-CONTROL][ERROR] ${msg}`, err || ''),
  warn: (msg, data) => console.warn(`[PLAN-CONTROL][WARN] ${msg}`, data || ''),
};

const storage = {
  get: (key) => { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) { LOG.error(`get ${key}`, e); return []; } },
  set: (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); return true; } catch (e) { LOG.error(`set ${key}`, e); return false; } },
};

const procesosGraficos = ["Impresión Offset", "Pre Prensa", "Troquelado", "Laminado", "Barnizado", "Corte", "Estuchado / Armado", "Control de Calidad", "Expedición", "Otro"];
const tiposCaracteristica = { critica: { label: "🔴 Crítica", color: "#e63946" }, mayor: { label: "🟡 Mayor", color: "#f59e0b" }, menor: { label: "🟢 Menor", color: "#22c55e" } };
const metodoControl = ["Visual", "Medición dimensional", "Ensayo funcional", "Colorimetría", "Gramaje / Báscula", "Comparación con muestra", "Check list", "Registro manual", "Otro"];
const frecuencias = ["Cada unidad", "Por muestra (cada 100)", "Por muestra (cada 500)", "Por turno", "Por lote", "Inicio de producción", "Al recibir material", "Continuo"];

function generarIdPC(proceso, lista) {
  const proc = proceso?.replace(/\s+/g, "").slice(0, 6).toUpperCase() || "GEN";
  return `PC-${proc}-${String(lista.length + 1).padStart(3, "0")}`;
}

function obtenerFMEA(idFMEA) {
  try { return storage.get(STORAGE_FMEA).find(f => f.idFMEA === idFMEA?.trim()) || null; }
  catch (e) { return null; }
}

function validarRelacionFMEA(idFMEA) {
  if (!idFMEA?.trim()) return { valido: false, mensaje: "Requerido — cada control debe estar vinculado a un FMEA" };
  const fmea = obtenerFMEA(idFMEA);
  if (!fmea) return { valido: false, mensaje: "El ID FMEA no existe en el sistema" };
  if (fmea.estado === "cerrado") return { valido: false, mensaje: `El FMEA ${idFMEA} está cerrado` };
  return { valido: true, fmea };
}

// ─── EXPORTACIÓN ──────────────────────────────────────────────
function exportarCSV(datos, nombre) {
  try {
    if (!datos.length) { alert("No hay datos para exportar."); return; }
    const keys = Object.keys(datos[0]).filter(k => !k.startsWith('_'));
    const csv = [keys.join(","), ...datos.map(d => keys.map(k => `"${String(d[k] || "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${nombre}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    LOG.info("CSV PC exportado", { registros: datos.length });
  } catch (err) { LOG.error("Error exportar CSV", err); }
}

function exportarPDF(datos, titulo) {
  try {
    if (!datos.length) { alert("No hay datos para exportar."); return; }
    const keys = ["idPC", "idFMEARelacionado", "proceso", "producto", "caracteristica", "tipoCaracteristica", "metodo", "frecuencia", "responsable", "limiteAceptable", "desviosCount"];
    const filas = datos.map(d => `<tr>${keys.map(k => `<td style="padding:4px 6px;border-bottom:.5px solid #ddd;font-size:9px">${d[k] || "—"}</td>`).join("")}</tr>`).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${titulo}</title>
    <style>body{font-family:Arial,sans-serif;font-size:10px;padding:20px}
    h1{color:#09184D;font-size:16px}h2{color:#7B5BF2;font-size:12px}
    table{width:100%;border-collapse:collapse;margin-top:16px}
    th{background:#09184D;color:white;padding:5px 6px;font-size:9px;text-align:left}
    tr:nth-child(even){background:#f7f7f7}</style></head>
    <body><h1>ALQOS™ — ${titulo}</h1>
    <h2>Plan de Control de Procesos</h2>
    <p style="font-size:10px;color:#808085">Generado: ${new Date().toLocaleString('es-AR')} | Total: ${datos.length} | Críticos: ${datos.filter(d => d.tipoCaracteristica === "critica").length}</p>
    <table><thead><tr>${keys.map(k => `<th>${k}</th>`).join("")}</tr></thead>
    <tbody>${filas}</tbody></table>
    <p style="font-size:9px;color:#808085;margin-top:20px">ALQOS™ Plan de Control — Confidencial — ISO 9001:2015 §8.5</p>
    </body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html); w.document.close();
    setTimeout(() => w.print(), 500);
    LOG.info("PDF PC generado", { registros: datos.length });
  } catch (err) { LOG.error("Error exportar PDF", err); }
}

function generarNCDesdeDesvio(planControl, usuario) {
  try {
    const ncs = storage.get(STORAGE_NC);
    const anio = new Date().getFullYear();
    const nci = `NCI-${anio}-${String(ncs.length + 1).padStart(3, "0")}`;
    const escalamientoMap = { critica: "alto", mayor: "medio", menor: "bajo" };
    const nueva = {
      nci, id: Date.now(),
      origen: "Plan de Control — Desvío automático",
      idPlanControl: planControl.idPC,
      idFMEARelacionado: planControl.idFMEARelacionado,
      idRiesgoFMEA: planControl.idRiesgo || planControl.idFMEARelacionado,
      maquinaId: planControl.maquinaId || null,
      maquinaNombre: planControl.maquinaNombre || null,
      descripcion: `Desvío en control: "${planControl.caracteristica}" | Proceso: ${planControl.proceso} | Producto: ${planControl.producto || "—"} | Límite: ${planControl.limiteAceptable}`,
      proceso: planControl.proceso, producto: planControl.producto,
      escalamiento: escalamientoMap[planControl.tipoCaracteristica] || "medio",
      tipoCaracteristica: planControl.tipoCaracteristica,
      accionInmediata: planControl.accionDesvio,
      modoFallaFMEA: planControl.modoFallaFMEA || "",
      estado: "abierta", fechaHora: new Date().toISOString(),
      creadoPor: usuario?.nombre || "Sistema", rolCreador: usuario?.rol || "",
      generadaAutomaticamente: true, _source: "localStorage", _schema: "nc_v1", _generadaDesde: "plancontrol",
    };
    const ok = storage.set(STORAGE_NC, [...ncs, nueva]);
    if (!ok) throw new Error("Error al guardar NC");
    LOG.info("NC generada", { nci });
    return nci;
  } catch (err) { LOG.error("Error al generar NC", err); return null; }
}

export default function PlanControl({ onVolver, oscuro, usuario }) {
  const [controles, setControles] = useState(() => storage.get(STORAGE_PC));
  const [vista, setVista] = useState("listado");
  const [form, setForm] = useState({ proceso: "Impresión Offset", tipoCaracteristica: "mayor", metodo: "Visual", frecuencia: "Por turno" });
  const [detalle, setDetalle] = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [errores, setErrores] = useState({});
  const [ncGenerada, setNcGenerada] = useState(null);
  // Catálogo de máquinas para vincular un control a un equipo puntual del
  // proceso -- una característica ("Registro ±0.5mm") puede tener límites o
  // comportamiento distinto entre máquinas del mismo proceso genérico (ej.
  // Offset 1 Heidelberg vs Offset 2 Komori). Mismo patrón de solo lectura
  // ya probado en TPMMantenimiento.js.
  const [maquinas, setMaquinas] = useState([]);
  useEffect(() => {
    async function cargarMaquinas() {
      try {
        const { supabase } = await import('./supabase');
        const { data } = await supabase.from('maquinas').select('data');
        if (data?.length) setMaquinas(data.map(r => r.data));
        else setMaquinas(storage.get(STORAGE_MAQUINAS));
      } catch (e) { setMaquinas(storage.get(STORAGE_MAQUINAS)); }
    }
    cargarMaquinas();
  }, []);

  const bg = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg = oscuro ? "#0d2160" : "white";
  const texto = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde = oscuro ? "#1e3a8a" : "#eee";
  const navBg = oscuro ? "#060f30" : "#09184D";
  const subtexto = oscuro ? "#9aabd4" : "#808085";
  const esAdmin = usuario?.rol === "Administrador";

  const inp = { width: "100%", padding: "8px 12px", borderRadius: "8px", border: `1px solid ${borde}`, fontSize: "13px", boxSizing: "border-box", marginTop: "4px", background: cardBg, color: texto, fontFamily: "sans-serif" };
  const lbl = { fontSize: "12px", color: oscuro ? "#9aabd4" : "#555", fontWeight: "600", display: "block", marginTop: "12px" };
  const card = { background: cardBg, borderRadius: "12px", padding: "1.5rem", border: `1px solid ${borde}`, marginBottom: "12px" };

  const validar = () => {
    const e = {};
    if (!form.producto?.trim()) e.producto = "Requerido";
    if (!form.caracteristica?.trim()) e.caracteristica = "Requerido";
    if (!form.responsable?.trim()) e.responsable = "Requerido";
    if (!form.limiteAceptable?.trim()) e.limiteAceptable = "Requerido";
    if (!form.accionDesvio?.trim()) e.accionDesvio = "Requerido";
    const relacion = validarRelacionFMEA(form.idFMEARelacionado);
    if (!relacion.valido) { e.idFMEARelacionado = relacion.mensaje; }
    else { setForm(f => ({ ...f, _modoFallaFMEA: relacion.fmea?.modofalla || "", idRiesgo: relacion.fmea?.idRiesgo || relacion.fmea?.idFMEA })); }
    setErrores(e);
    return Object.keys(e).length === 0;
  };

  const guardar = () => {
    if (!validar()) return;
    try {
      const controlesActuales = storage.get(STORAGE_PC);
      const duplicado = controlesActuales.find(c =>
        c.idFMEARelacionado === form.idFMEARelacionado &&
        c.caracteristica?.trim().toLowerCase() === form.caracteristica?.trim().toLowerCase() &&
        c.proceso === form.proceso
      );
      if (duplicado) { alert(`⚠️ Ya existe un control para esta característica (${duplicado.idPC}).`); return; }
      const nuevo = {
        ...form, id: Date.now(), idPC: generarIdPC(form.proceso, controlesActuales),
        registro: form.registro !== "no", activo: true, desviosCount: 0,
        modoFallaFMEA: form._modoFallaFMEA || "",
        fechaCreacion: new Date().toISOString(),
        creadoPor: usuario?.nombre || "Sistema", rolCreador: usuario?.rol || "",
        _source: "localStorage", _schema: "plancontrol_v1",
      };
      delete nuevo._modoFallaFMEA;
      const nuevos = [...controlesActuales, nuevo];
      if (!storage.set(STORAGE_PC, nuevos)) { alert("Error al guardar."); return; }
      subirDatos(STORAGE_PC);
      logAction("PlanControl", "crear", { entidad: "Control", idEntidad: nuevo.idPC, despues: { idPC: nuevo.idPC, proceso: nuevo.proceso, caracteristica: nuevo.caracteristica }, detalle: `Control creado vinculado a ${nuevo.idFMEARelacionado}` }, usuario);
      LOG.info("PC guardado", { id: nuevo.idPC });
      setControles(nuevos);
      setForm({ proceso: "Impresión Offset", tipoCaracteristica: "mayor", metodo: "Visual", frecuencia: "Por turno" });
      setErrores({}); setVista("listado");
    } catch (err) { LOG.error("Error al guardar PC", err); alert("Error inesperado."); }
  };

  const registrarDesvio = (control) => {
    try {
      const nci = generarNCDesdeDesvio(control, usuario);
      if (nci) {
        setNcGenerada(nci);
        const actualizados = controles.map(c => c.id === control.id ? { ...c, ultimoDesvio: new Date().toISOString(), ncGenerada: nci, desviosCount: (c.desviosCount || 0) + 1 } : c);
        setControles(actualizados);
        storage.set(STORAGE_PC, actualizados);
        subirDatos(STORAGE_PC);
        logAction("PlanControl", "desvio", { entidad: "Control", idEntidad: control.idPC, detalle: `Desvío registrado → NC ${nci}` }, usuario);
        if (detalle?.id === control.id) setDetalle(prev => ({ ...prev, ultimoDesvio: new Date().toISOString(), ncGenerada: nci, desviosCount: (prev.desviosCount || 0) + 1 }));
      } else { alert("No se pudo generar la NC."); }
    } catch (err) { LOG.error("Error al registrar desvío", err); }
  };

  const eliminar = async (id) => {
    const ctrl = controles.find(c => c.id === id);
    const nuevos = controles.filter(c => c.id !== id);
    setControles(nuevos);
    storage.set(STORAGE_PC, nuevos);
    await eliminarDato(STORAGE_PC, id);
    logAction("PlanControl", "eliminar", { entidad: "Control", idEntidad: ctrl?.idPC, antes: ctrl, detalle: "Control eliminado" }, usuario);
    setConfirmEliminar(null); setDetalle(null); setVista("listado");
  };

  const controlesFiltrados = filtroTipo === "todos" ? controles : controles.filter(c => c.tipoCaracteristica === filtroTipo);
  const resumen = {
    total: controles.length,
    criticos: controles.filter(c => c.tipoCaracteristica === "critica").length,
    mayores: controles.filter(c => c.tipoCaracteristica === "mayor").length,
    desviosRegistrados: controles.filter(c => c.ncGenerada).length,
  };

  const Header = ({ titulo, onBack, extra }) => (
    <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Volver</button>
        <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>{titulo}</span>
      </div>
      {extra}
    </div>
  );

  if (confirmEliminar) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: cardBg, borderRadius: "16px", padding: "2rem", maxWidth: "360px", width: "90%", border: `1px solid ${borde}`, textAlign: "center" }}>
        <p style={{ fontSize: "32px", marginBottom: "1rem" }}>✅</p>
        <p style={{ fontSize: "15px", fontWeight: "600", color: texto, marginBottom: ".5rem" }}>¿Eliminar este control?</p>
        <p style={{ fontSize: "13px", color: subtexto, marginBottom: "1.5rem" }}>Las NC generadas no se eliminarán.</p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <button onClick={() => setConfirmEliminar(null)} style={{ padding: "10px 20px", background: "none", border: `1px solid ${borde}`, borderRadius: "10px", cursor: "pointer", color: texto, fontSize: "13px" }}>Cancelar</button>
          <button onClick={() => eliminar(confirmEliminar)} style={{ padding: "10px 20px", background: "#e63946", border: "none", borderRadius: "10px", cursor: "pointer", color: "white", fontSize: "13px", fontWeight: "600" }}>Eliminar</button>
        </div>
      </div>
    </div>
  );

  if (vista === "detalle" && detalle) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo={detalle.idPC}
        onBack={() => { setDetalle(null); setVista("listado"); setNcGenerada(null); }}
        extra={
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={() => exportarPDF([detalle], `PC_${detalle.idPC}`)}
              style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 10px", borderRadius: "8px", cursor: "pointer", fontSize: "12px" }}>📄 PDF</button>
            {esAdmin && <button onClick={() => setConfirmEliminar(detalle.id)}
              style={{ background: "rgba(230,57,70,.2)", border: "1px solid rgba(230,57,70,.4)", color: "#e63946", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "12px" }}>🗑 Eliminar</button>}
          </div>
        }
      />
      <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
        {ncGenerada && (
          <div style={{ background: "#fff0f0", border: "2px solid #e63946", borderRadius: "12px", padding: "1rem", marginBottom: "12px", textAlign: "center" }}>
            <p style={{ fontSize: "14px", fontWeight: "700", color: "#e63946", margin: "0 0 4px" }}>⚠️ NC generada automáticamente</p>
            <p style={{ fontSize: "13px", color: "#2F3E2E", margin: 0 }}>Código: <strong>{ncGenerada}</strong> — Revisá el módulo de No Conformidades</p>
          </div>
        )}
        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#F0EEFF", border: `1px solid ${tiposCaracteristica[detalle.tipoCaracteristica]?.color || "#7B5BF2"}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#7B5BF2", margin: 0 }}>Datos del control</h3>
            <span style={{ fontSize: "12px", fontWeight: "700", padding: "3px 10px", borderRadius: "10px", background: `${tiposCaracteristica[detalle.tipoCaracteristica]?.color}22`, color: tiposCaracteristica[detalle.tipoCaracteristica]?.color, border: `1px solid ${tiposCaracteristica[detalle.tipoCaracteristica]?.color}` }}>
              {tiposCaracteristica[detalle.tipoCaracteristica]?.label}
            </span>
          </div>
          {[["ID Plan de Control", detalle.idPC], ["ID FMEA vinculado", detalle.idFMEARelacionado], ["ID Riesgo", detalle.idRiesgo], ["Proceso", detalle.proceso], ["Máquina vinculada", detalle.maquinaNombre], ["Producto", detalle.producto], ["Característica", detalle.caracteristica], ["Método", detalle.metodo], ["Frecuencia", detalle.frecuencia], ["Responsable", detalle.responsable], ["Límite aceptable", detalle.limiteAceptable], ["Requiere registro", detalle.registro ? "Sí" : "No"], ["Desvíos registrados", String(detalle.desviosCount || 0)], ["Creado por", `${detalle.creadoPor} — ${detalle.rolCreador}`]].map(([k, v]) => v !== undefined && v !== null && (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "4px 0", borderBottom: `0.5px solid ${borde}` }}>
              <span style={{ color: subtexto }}>{k}</span>
              <span style={{ color: k === "ID FMEA vinculado" || k === "ID Riesgo" ? "#7B5BF2" : texto, fontWeight: k === "ID FMEA vinculado" ? "700" : "500", maxWidth: "60%", textAlign: "right" }}>{v}</span>
            </div>
          ))}
        </div>
        {detalle.modoFallaFMEA && (
          <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#f0f8ff", border: `1px solid ${oscuro ? "#1e3a8a" : "#bde0fe"}` }}>
            <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#3b82f6", marginBottom: "4px" }}>🔗 Modo de falla del FMEA vinculado</h3>
            <p style={{ fontSize: "13px", color: texto, margin: 0 }}>{detalle.modoFallaFMEA}</p>
          </div>
        )}
        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#fff8f0", border: `1px solid ${oscuro ? "#f59e0b44" : "#fde68a"}` }}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#f59e0b", marginBottom: "8px" }}>Acción ante desvío</h3>
          <p style={{ fontSize: "13px", color: texto, margin: "0 0 12px" }}>{detalle.accionDesvio}</p>
          {detalle.ultimoDesvio && <p style={{ fontSize: "11px", color: "#e63946", margin: "0 0 4px" }}>⚠️ Último desvío: {detalle.ultimoDesvio?.slice(0, 16).replace("T", " ")} · Total: {detalle.desviosCount || 1}</p>}
          {detalle.ncGenerada && <p style={{ fontSize: "11px", color: "#e63946", margin: 0 }}>NC generada: <strong>{detalle.ncGenerada}</strong></p>}
        </div>
        {esAdmin && (
          <button onClick={() => registrarDesvio(detalle)}
            style={{ width: "100%", padding: "14px", background: "#e63946", color: "white", border: "none", borderRadius: "10px", fontSize: "14px", fontWeight: "600", cursor: "pointer", marginBottom: "12px" }}>
            🚨 Registrar desvío — Generar NC automáticamente
          </button>
        )}
      </div>
    </div>
  );

  if (vista === "nueva") return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo="Nuevo Control" onBack={() => { setVista("listado"); setErrores({}); }} />
      <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#F0EEFF", border: `1px solid ${oscuro ? "#7B5BF2" : "#d4c8ff"}` }}>
          <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#7B5BF2", marginBottom: "4px" }}>Trazabilidad automática</h3>
          <p style={{ fontSize: "12px", color: subtexto, margin: 0 }}>Creado por: <strong style={{ color: texto }}>{usuario?.nombre}</strong> — {usuario?.rol}</p>
        </div>
        <div style={{ ...card, background: oscuro ? "#0a0a2a" : "#fff0f0", border: "1px solid #e6394644" }}>
          <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#e63946", marginBottom: "6px" }}>⚠️ Vinculación con FMEA obligatoria</h3>
          <span style={lbl}>ID FMEA relacionado *</span>
          <input value={form.idFMEARelacionado || ""} onChange={e => setForm(f => ({ ...f, idFMEARelacionado: e.target.value }))} style={{ ...inp, borderColor: errores.idFMEARelacionado ? "#e63946" : borde }} placeholder="Ej: FMEA-IMPRES-001" />
          {errores.idFMEARelacionado && <p style={{ color: "#e63946", fontSize: "11px", margin: "4px 0 0" }}>{errores.idFMEARelacionado}</p>}
          {form.idFMEARelacionado && !errores.idFMEARelacionado && obtenerFMEA(form.idFMEARelacionado) && (
            <div style={{ marginTop: "8px", padding: "8px 10px", background: "#22c55e22", border: "1px solid #22c55e", borderRadius: "8px" }}>
              <p style={{ fontSize: "12px", color: "#22c55e", fontWeight: "600", margin: "0 0 2px" }}>✅ FMEA encontrado</p>
              <p style={{ fontSize: "11px", color: subtexto, margin: 0 }}>Modo de falla: {obtenerFMEA(form.idFMEARelacionado)?.modofalla}</p>
            </div>
          )}
        </div>
        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#09184D" }}>Identificación del control</h3>
          <span style={lbl}>Proceso *</span>
          <select onChange={e => setForm(f => ({ ...f, proceso: e.target.value }))} style={inp} defaultValue="Impresión Offset">
            {procesosGraficos.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {maquinas.length > 0 && (
            <>
              <span style={lbl}>Vincular a máquina cargada (opcional — habilita comparar controles entre equipos del mismo proceso)</span>
              <select onChange={e => {
                const id = e.target.value ? Number(e.target.value) : null;
                const m = maquinas.find(x => x.id === id);
                setForm(f => ({ ...f, maquinaId: id, maquinaNombre: m ? (m.nombre + (m.modelo ? ` — ${m.modelo}` : "")) : null }));
              }} style={inp}>
                <option value="">— Sin vincular —</option>
                {maquinas.map(m => <option key={m.id} value={m.id}>{m.nombre}{m.modelo ? ` — ${m.modelo}` : ""}</option>)}
              </select>
            </>
          )}
          <span style={lbl}>Tipo de característica *</span>
          <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
            {Object.entries(tiposCaracteristica).map(([val, { label, color }]) => (
              <button key={val} onClick={() => setForm(f => ({ ...f, tipoCaracteristica: val }))}
                style={{ flex: 1, padding: "8px", borderRadius: "10px", fontSize: "12px", cursor: "pointer", fontWeight: "600", border: form.tipoCaracteristica === val ? `2px solid ${color}` : `1px solid ${borde}`, background: form.tipoCaracteristica === val ? `${color}22` : cardBg, color: form.tipoCaracteristica === val ? color : texto }}>
                {label}
              </button>
            ))}
          </div>
          {[{ key: "producto", label: "Producto *" }, { key: "caracteristica", label: "Característica a controlar *", placeholder: "Ej: Registro de impresión ±0.5mm" }].map(({ key, label, placeholder }) => (
            <div key={key}>
              <span style={lbl}>{label}</span>
              <input onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={{ ...inp, borderColor: errores[key] ? "#e63946" : borde }} placeholder={placeholder || ""} />
              {errores[key] && <p style={{ color: "#e63946", fontSize: "11px", margin: "2px 0 0" }}>{errores[key]}</p>}
            </div>
          ))}
          <span style={lbl}>Método de control *</span>
          <select onChange={e => setForm(f => ({ ...f, metodo: e.target.value }))} style={inp} defaultValue="Visual">
            {metodoControl.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <span style={lbl}>Frecuencia *</span>
          <select onChange={e => setForm(f => ({ ...f, frecuencia: e.target.value }))} style={inp} defaultValue="Por turno">
            {frecuencias.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <span style={lbl}>Responsable *</span>
          <input onChange={e => setForm(f => ({ ...f, responsable: e.target.value }))} style={{ ...inp, borderColor: errores.responsable ? "#e63946" : borde }} />
          {errores.responsable && <p style={{ color: "#e63946", fontSize: "11px", margin: "2px 0 0" }}>{errores.responsable}</p>}
          <span style={lbl}>Límite aceptable *</span>
          <input onChange={e => setForm(f => ({ ...f, limiteAceptable: e.target.value }))} style={{ ...inp, borderColor: errores.limiteAceptable ? "#e63946" : borde }} placeholder="Ej: ±0.5mm / NQA 1.0" />
          {errores.limiteAceptable && <p style={{ color: "#e63946", fontSize: "11px", margin: "2px 0 0" }}>{errores.limiteAceptable}</p>}
          <span style={lbl}>Acción ante desvío *</span>
          <textarea rows={2} onChange={e => setForm(f => ({ ...f, accionDesvio: e.target.value }))} style={{ ...inp, resize: "none", borderColor: errores.accionDesvio ? "#e63946" : borde }} placeholder="Qué hacer si no se cumple el límite..." />
          {errores.accionDesvio && <p style={{ color: "#e63946", fontSize: "11px", margin: "2px 0 0" }}>{errores.accionDesvio}</p>}
          <span style={lbl}>Requiere registro documentado</span>
          <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
            {[["si", "✅ Sí"], ["no", "❌ No"]].map(([val, lab]) => (
              <button key={val} onClick={() => setForm(f => ({ ...f, registro: val }))}
                style={{ flex: 1, padding: "8px", borderRadius: "10px", fontSize: "13px", cursor: "pointer", fontWeight: "600", border: form.registro === val ? "2px solid #7B5BF2" : `1px solid ${borde}`, background: form.registro === val ? "rgba(123,91,242,.1)" : cardBg, color: form.registro === val ? "#7B5BF2" : texto }}>
                {lab}
              </button>
            ))}
          </div>
        </div>
        <button onClick={guardar} style={{ width: "100%", padding: "14px", background: "#7B5BF2", color: "white", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: "600", cursor: "pointer", marginTop: "1rem" }}>
          Guardar Plan de Control
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={onVolver} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Menú</button>
          <BotonInstructivo moduloKey="plan_control" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="plan_control" oscuro={oscuro} />}
          <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>Plan de Control</span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={() => exportarCSV(controlesFiltrados, "PlanControl")}
            style={{ padding: "6px 10px", background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: "white" }}>📊 Excel</button>
          <button onClick={() => exportarPDF(controlesFiltrados, "Plan de Control")}
            style={{ padding: "6px 10px", background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: "white" }}>📄 PDF</button>
          {esAdmin && <button onClick={() => setVista("nueva")}
            style={{ padding: "6px 14px", background: "#7B5BF2", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>+ Nuevo control</button>}
        </div>
      </div>
      <div style={{ padding: "1rem 1.5rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "10px", marginBottom: "1.5rem" }}>
          {[["Total controles", resumen.total, "#7B5BF2"], ["Críticos", resumen.criticos, "#e63946"], ["Mayores", resumen.mayores, "#f59e0b"], ["Con desvíos", resumen.desviosRegistrados, "#3b82f6"]].map(([lab, val, col]) => (
            <div key={lab} style={{ background: cardBg, borderRadius: "12px", padding: "1rem", border: `1px solid ${lab === "Críticos" && resumen.criticos > 0 ? "#e63946" : borde}`, textAlign: "center" }}>
              <p style={{ fontSize: "22px", fontWeight: "700", color: col, margin: 0 }}>{val}</p>
              <p style={{ fontSize: "11px", color: subtexto, margin: "4px 0 0" }}>{lab}</p>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "1rem" }}>
          <button onClick={() => setFiltroTipo("todos")} style={{ padding: "4px 10px", borderRadius: "16px", fontSize: "11px", cursor: "pointer", fontWeight: "600", border: filtroTipo === "todos" ? "2px solid #7B5BF2" : `1px solid ${borde}`, background: filtroTipo === "todos" ? "rgba(123,91,242,.1)" : cardBg, color: filtroTipo === "todos" ? "#7B5BF2" : subtexto }}>Todos</button>
          {Object.entries(tiposCaracteristica).map(([val, { label, color }]) => (
            <button key={val} onClick={() => setFiltroTipo(val)} style={{ padding: "4px 10px", borderRadius: "16px", fontSize: "11px", cursor: "pointer", fontWeight: "600", border: filtroTipo === val ? `2px solid ${color}` : `1px solid ${borde}`, background: filtroTipo === val ? `${color}22` : cardBg, color: filtroTipo === val ? color : subtexto }}>{label}</button>
          ))}
        </div>
        {controlesFiltrados.length === 0
          ? <div style={{ textAlign: "center", padding: "4rem" }}>
              <p style={{ fontSize: "40px" }}>✅</p>
              <p style={{ fontSize: "15px", color: texto }}>No hay controles registrados</p>
              <p style={{ fontSize: "13px", color: subtexto }}>Creá primero un FMEA y luego vinculá un control</p>
            </div>
          : controlesFiltrados.map(c => (
              <div key={c.id} onClick={() => { setDetalle(c); setVista("detalle"); setNcGenerada(null); }}
                style={{ background: c.tipoCaracteristica === "critica" ? (oscuro ? "#1a0505" : "#fff5f5") : cardBg, borderRadius: "12px", padding: "1.2rem 1.5rem", border: `1px solid ${c.tipoCaracteristica === "critica" ? "#e63946" : borde}`, marginBottom: "10px", cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(123,91,242,0.15)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <span style={{ fontWeight: "700", color: "#7B5BF2", fontSize: "13px" }}>{c.idPC}</span>
                    <span style={{ marginLeft: "8px", fontSize: "11px", fontWeight: "700", color: tiposCaracteristica[c.tipoCaracteristica]?.color, background: `${tiposCaracteristica[c.tipoCaracteristica]?.color}22`, padding: "2px 6px", borderRadius: "6px" }}>{tiposCaracteristica[c.tipoCaracteristica]?.label}</span>
                    <p style={{ fontSize: "13px", color: texto, fontWeight: "600", marginTop: "4px" }}>{c.caracteristica}</p>
                    <p style={{ fontSize: "12px", color: subtexto, marginTop: "2px" }}>{c.proceso}{c.maquinaNombre ? ` · ⚙️ ${c.maquinaNombre}` : ""} · {c.metodo} · {c.frecuencia}</p>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
                    <span style={{ fontSize: "11px", color: "#7B5BF2", fontWeight: "600" }}>FMEA: {c.idFMEARelacionado}</span>
                    {c.desviosCount > 0 && <span style={{ fontSize: "10px", color: "#f59e0b", fontWeight: "600" }}>⚠️ {c.desviosCount} desvío{c.desviosCount > 1 ? "s" : ""}</span>}
                    {c.ncGenerada && <span style={{ fontSize: "10px", color: "#e63946", fontWeight: "600" }}>NC: {c.ncGenerada}</span>}
                  </div>
                </div>
                <div style={{ marginTop: "6px", fontSize: "11px", color: oscuro ? "#7B5BF2" : "#9b8cc4" }}>
                  Responsable: {c.responsable} · Límite: {c.limiteAceptable}
                </div>
              </div>
            ))
        }
      </div>
    </div>
  );
}